import _extends from "@babel/runtime/helpers/extends";
var _DomainActionsErrorsM, _DomainActionsErrorsM2, _DomainActionsErrorsM3;
import { Box } from '@wix/design-system';
import { CoreDomainSearchStore, DomainActionsErrorsMap, DomainSearchSuiteModule } from '@wix/domains-search-suite';
import { WIX_HEADER_HEIGHT } from '@wix/core-domain-search';
import { isValidDomain } from '@wix/domains-serverless-interface';
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { GeneralAnonymousOverride, SubdomainRootTaken } from '../../../Components';
import { AnonymousFooter } from '../../../Components/AnonymousFooter/AnonymousFooter';
import { BngSearchField, BngSearchFieldMobile } from '../../../Components/BngSearchField';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { BusinessNamesSection } from '../../../Components/BusinessNames';
import { BngResultsHeader } from '../../../Components/Headers';
import BngDomainSuggestionsSection from './Components/BngDomainSuggestionsSection';
import BngPageLoadError from './Components/BngPageLoadError';
import { RecommendationsError } from '../../../Components/Recommendations';
import { useDomainSearchGateBi } from '../../../hooks/useDomainSearchGateBi';
import { useBusinessNames } from '../../../hooks/useBusinessNames';
import { useRecommendations } from '../../../hooks/useRecommendations';
import { useSettledMethods } from '../../../hooks/useSettledMethod';
import { ANONYMOUS_SETTLEMENTS } from '../../../utils/anonymousSettlements';
import { useResultsPageOfferingsBi } from '../../../hooks/useResultsPageOfferingsBi';
import { DomainSearchGateModule } from '../../../module/domainSearchGate.module';
import { ViewMode } from '../../../types';
import pageStyles from '../../anonymousResultsPage.scss';
const customConfiguration = {
  ...(((_DomainActionsErrorsM = DomainActionsErrorsMap.connect) == null ? void 0 : _DomainActionsErrorsM.reduce((acc, status) => ({
    ...acc,
    [status]: GeneralAnonymousOverride
  }), {})) ?? {}),
  ...(((_DomainActionsErrorsM2 = DomainActionsErrorsMap.assign) == null ? void 0 : _DomainActionsErrorsM2.reduce((acc, status) => ({
    ...acc,
    [status]: GeneralAnonymousOverride
  }), {})) ?? {}),
  ...(((_DomainActionsErrorsM3 = DomainActionsErrorsMap.transfer) == null ? void 0 : _DomainActionsErrorsM3.reduce((acc, status) => ({
    ...acc,
    [status]: GeneralAnonymousOverride
  }), {})) ?? {}),
  transfer: GeneralAnonymousOverride,
  connect: GeneralAnonymousOverride,
  subdomainRootIsNotConnected: SubdomainRootTaken,
  domainTakenInWix: GeneralAnonymousOverride,
  subdomainTakenInWix: SubdomainRootTaken,
  noStatus: GeneralAnonymousOverride
};
const syncQueryToStore = query => {
  CoreDomainSearchStore.setTlds([]);
  CoreDomainSearchStore.setQuery(query);
  CoreDomainSearchStore.setStyle('');
  CoreDomainSearchStore.setKeywords([]);
  CoreDomainSearchStore.setSubmittedFilters({
    query,
    tlds: [],
    keywords: [],
    style: ''
  });
  if (isValidDomain(query)) {
    CoreDomainSearchStore.setSubmittedDomain(query);
  } else {
    CoreDomainSearchStore.setSubmittedDomain(undefined);
  }
};
const DOMAINS_HEADING_SCROLL_PADDING_PX = 8;
const DESKTOP_STICKY_SEARCH_TOP_GAP_PX = 28;
const MOBILE_STICKY_SEARCH_TOP_GAP_PX = 10;
const MOBILE_STICKY_SEARCH_BOTTOM_GAP_PX = 10;
const MOBILE_DOMAINS_STEP_STICKY_SEARCH_BOTTOM_GAP_PX = 28;
const WIX_HEADER_HEIGHT_PX = parseInt(WIX_HEADER_HEIGHT, 10);
const scrollElementBelowStickyHeader = function (target, stickyHeader, stickyHeaderOffset) {
  if (stickyHeaderOffset === void 0) {
    stickyHeaderOffset = 0;
  }
  const headerHeight = (stickyHeader == null ? void 0 : stickyHeader.getBoundingClientRect().height) ?? 0;
  const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - stickyHeaderOffset - DOMAINS_HEADING_SCROLL_PADDING_PX;
  window.scrollTo({
    top: Math.max(0, top),
    behavior: 'smooth'
  });
};
const runAfterNextPaint = callback => {
  requestAnimationFrame(() => {
    requestAnimationFrame(callback);
  });
};
export const BngResultsPage = _ref => {
  let {
    handlers,
    externalViewer,
    showCartHeader
  } = _ref;
  useSettledMethods(ANONYMOUS_SETTLEMENTS);
  return /*#__PURE__*/React.createElement(DomainsApiClient.Provider, null, /*#__PURE__*/React.createElement(DomainSearchSuiteModule.Provider, {
    translations: {
      Loading: /*#__PURE__*/React.createElement(React.Fragment, null)
    },
    actions: {
      siteInfo: {
        externalViewer: Boolean(externalViewer)
      },
      handlers
    },
    statusesCustomComponents: {
      customConfiguration
    }
  }, /*#__PURE__*/React.createElement(BngResultsPageContent, {
    showCartHeader: showCartHeader
  })));
};
const BngResultsPageContent = _ref2 => {
  let {
    showCartHeader = false
  } = _ref2;
  DomainSearchGateModule.SDK.hooks.useDomainsSearchGateBM();
  const isMobile = useIsMobile();
  const twoStep = isMobile;
  const [mobileStep, setMobileStep] = useState('names');
  // Toggled on back navigation to re-trigger the names step slide-in.
  const [namesEntering, setNamesEntering] = useState(false);
  // Clips horizontal overflow only while a step slide-in plays (see .bngSwipeClip).
  const [stepAnimating, setStepAnimating] = useState(false);
  const history = useHistory();
  const {
    search
  } = useLocation();
  const queryFromUrl = useMemo(() => {
    var _URLSearchParams$get;
    return ((_URLSearchParams$get = new URLSearchParams(search).get('query')) == null ? void 0 : _URLSearchParams$get.trim()) ?? '';
  }, [search]);
  const isResultsVisible = !twoStep || mobileStep === 'domains';
  useDomainSearchGateBi({
    viewMode: ViewMode.STOREFRONT_BNG,
    deferResultsPageLoad: twoStep,
    isResultsVisible,
    bngMobileSearchTerm: twoStep ? queryFromUrl : undefined
  });
  const lastShownNamesRef = useRef([]);
  const firedNamesPromptRef = useRef(undefined);
  const [inputValue, setInputValue] = useState(queryFromUrl);
  const {
    hasError: hasBusinessNamesError,
    isReady: isBusinessNamesReady,
    error: businessNamesError,
    retry: retryBusinessNames
  } = useBusinessNames({
    query: queryFromUrl,
    enabled: Boolean(queryFromUrl)
  });
  const {
    recommendations,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    error,
    biError,
    onRetry,
    taxesNotIncluded,
    isPricingLoading
  } = useRecommendations({
    limit: 10,
    enableOnEmptyQuery: false,
    shouldFetchRecommendations: Boolean(queryFromUrl) && isBusinessNamesReady
  });
  const suggestionsIsLoading = queryFromUrl ? !isBusinessNamesReady || isLoading : false;
  useEffect(() => {
    setInputValue(queryFromUrl);
  }, [queryFromUrl]);
  useEffect(() => {
    if (!queryFromUrl) {
      return;
    }
    syncQueryToStore(queryFromUrl);
  }, [queryFromUrl]);
  useEffect(() => {
    if (twoStep) {
      setMobileStep('names');
    }
  }, [queryFromUrl, twoStep]);
  useEffect(() => {
    if (!stepAnimating) {
      return;
    }
    // Release just after the 0.28s slide-in finishes.
    const timeoutId = window.setTimeout(() => setStepAnimating(false), 320);
    return () => window.clearTimeout(timeoutId);
  }, [stepAnimating]);
  useResultsPageOfferingsBi({
    recommendations,
    isLoading,
    includeHeroCardOffers: true,
    biError,
    enabled: isResultsVisible
  });
  useEffect(() => {
    return () => {
      CoreDomainSearchStore.resetStore();
    };
  }, []);
  const handleBackClick = useCallback(() => {
    history.goBack();
  }, [history]);
  const handleGenerate = useCallback(() => {
    const nextQuery = inputValue.trim();
    if (!nextQuery) {
      return;
    }
    DomainSearchGateModule.internals.bi.onBngGeneralAction == null || DomainSearchGateModule.internals.bi.onBngGeneralAction({
      action: 'generate',
      value: nextQuery,
      button_name: 'generate',
      domain: '',
      search_term: nextQuery
    });
    syncQueryToStore(nextQuery);
    if (twoStep) {
      setMobileStep('names');
    }
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    history.push(`/?query=${encodeURIComponent(nextQuery)}`);
  }, [history, inputValue, twoStep]);
  const stickyHeaderRef = React.useRef(null);
  const domainsHeadingRef = React.useRef(null);
  const handleSelectedNameChange = useCallback(name => {
    syncQueryToStore(name);
  }, []);
  const handleGenerateMoreBi = useCallback(() => {
    DomainSearchGateModule.internals.bi.onBngGeneralAction == null || DomainSearchGateModule.internals.bi.onBngGeneralAction({
      action: 'generate',
      button_name: 'generate more names',
      domain: '',
      search_term: queryFromUrl
    });
  }, [queryFromUrl]);
  const handleRegenerateBi = useCallback(() => {
    DomainSearchGateModule.internals.bi.onBngGeneralAction == null || DomainSearchGateModule.internals.bi.onBngGeneralAction({
      action: 'generate',
      button_name: 'regenerate new names',
      domain: '',
      search_term: queryFromUrl
    });
  }, [queryFromUrl]);
  const handleTileTooltipView = useCallback(name => {
    DomainSearchGateModule.internals.bi.onBngTooltipView == null || DomainSearchGateModule.internals.bi.onBngTooltipView({
      action: 'view',
      value: name,
      domain: name,
      search_term: queryFromUrl
    });
  }, [queryFromUrl]);
  const handleUserSelectedName = useCallback(name => {
    DomainSearchGateModule.internals.bi.onBngGeneralAction == null || DomainSearchGateModule.internals.bi.onBngGeneralAction({
      action: 'select',
      value: name,
      domain: name,
      search_term: queryFromUrl
    });
    if (twoStep) {
      syncQueryToStore(name);
      setNamesEntering(false);
      setStepAnimating(true);
      setMobileStep('domains');
      window.scrollTo({
        top: 0
      });
      return;
    }
    runAfterNextPaint(() => {
      const domainsHeading = domainsHeadingRef.current;
      if (!domainsHeading) {
        return;
      }
      scrollElementBelowStickyHeader(domainsHeading, stickyHeaderRef.current, WIX_HEADER_HEIGHT_PX);
    });
  }, [twoStep, queryFromUrl]);
  const handleNamesShown = useCallback(shownNames => {
    const nameStrings = shownNames.map(n => n.name).filter(name => Boolean(name));
    lastShownNamesRef.current = nameStrings;
    if (firedNamesPromptRef.current === queryFromUrl) {
      return;
    }
    firedNamesPromptRef.current = queryFromUrl;
    DomainSearchGateModule.internals.bi.onBngBusinessNamesShown == null || DomainSearchGateModule.internals.bi.onBngBusinessNamesShown({
      searchTerm: queryFromUrl,
      searchOrigin: ViewMode.STOREFRONT_BNG,
      names: JSON.stringify(nameStrings),
      namesCount: nameStrings.length,
      trigger: 'search_submit'
    });
  }, [queryFromUrl]);
  const handleBackToNames = useCallback(() => {
    DomainSearchGateModule.internals.bi.onBngBusinessNamesShown == null || DomainSearchGateModule.internals.bi.onBngBusinessNamesShown({
      searchTerm: queryFromUrl,
      searchOrigin: ViewMode.STOREFRONT_BNG,
      names: JSON.stringify(lastShownNamesRef.current),
      namesCount: lastShownNamesRef.current.length,
      trigger: 'back_navigation'
    });
    DomainSearchGateModule.internals.bi.onBngGeneralAction == null || DomainSearchGateModule.internals.bi.onBngGeneralAction({
      action: 'back',
      button_name: 'back',
      domain: '',
      search_term: queryFromUrl
    });
    setNamesEntering(true);
    setStepAnimating(true);
    setMobileStep('names');
  }, [queryFromUrl]);
  const InputComponent = isMobile ? BngSearchFieldMobile : BngSearchField;
  const mobileStickySearchBottomGap = mobileStep === 'domains' ? MOBILE_DOMAINS_STEP_STICKY_SEARCH_BOTTOM_GAP_PX : MOBILE_STICKY_SEARCH_BOTTOM_GAP_PX;
  if (businessNamesError === 'NetworkIssue') {
    return /*#__PURE__*/React.createElement(BngPageLoadError, {
      onRetry: retryBusinessNames,
      message: "domain_search_gate.bng_results.error.full_page.connection.description"
    });
  }
  if (businessNamesError === 'ServerError') {
    return /*#__PURE__*/React.createElement(BngPageLoadError, {
      onRetry: retryBusinessNames,
      message: "domain_search_gate.bng_results.error.full_page.description"
    });
  }
  return /*#__PURE__*/React.createElement(Box, {
    display: "block",
    boxSizing: "border-box",
    width: "100%",
    className: twoStep && stepAnimating ? pageStyles.bngSwipeClip : undefined
  }, /*#__PURE__*/React.createElement(Box, _extends({
    align: "center",
    className: `${pageStyles.resultsPage} ${pageStyles.noInsights} ${pageStyles.withNames}`,
    gap: isMobile ? 0 : 'SP6 0',
    height: "100%",
    backgroundColor: "white",
    overflow: "visible",
    padding: isMobile ? 'SP5 SP4' : 'SP5 48px'
  }, showCartHeader && {
    paddingTop: 0
  }), /*#__PURE__*/React.createElement(Box, {
    boxSizing: "border-box",
    align: "center",
    gridArea: "header",
    width: "100%",
    backgroundColor: "white",
    direction: "vertical",
    gap: "SP6",
    paddingBottom: isMobile ? 'SP3' : undefined
  }, /*#__PURE__*/React.createElement(BngResultsHeader, {
    onBackClick: handleBackClick
  })), /*#__PURE__*/React.createElement(Box, {
    ref: stickyHeaderRef,
    boxSizing: "border-box",
    align: "center",
    gridArea: "searchbar",
    top: WIX_HEADER_HEIGHT,
    position: "sticky",
    width: "100%",
    zIndex: 11,
    backgroundColor: "white",
    direction: "vertical",
    paddingTop: isMobile ? `${MOBILE_STICKY_SEARCH_TOP_GAP_PX}px` : `${DESKTOP_STICKY_SEARCH_TOP_GAP_PX}px`,
    paddingBottom: isMobile ? `${mobileStickySearchBottomGap}px` : '0px',
    marginTop: isMobile ? undefined : `-${DESKTOP_STICKY_SEARCH_TOP_GAP_PX}px`
  }, /*#__PURE__*/React.createElement("div", {
    className: pageStyles.centeredContent
  }, /*#__PURE__*/React.createElement(InputComponent, {
    value: inputValue,
    onChange: setInputValue,
    onSubmit: handleGenerate
  }))), queryFromUrl && /*#__PURE__*/React.createElement(Box, {
    gridArea: "names",
    className: `${pageStyles.centeredContent}${twoStep && namesEntering ? ` ${pageStyles.bngNamesStepEnter}` : ''}`,
    display: !twoStep || mobileStep === 'names' ? 'block' : 'none'
    // 6px + the 24px grid row-gap = 30px above the names title.
    ,
    paddingTop: isMobile ? 'SP3' : '6px'
  }, /*#__PURE__*/React.createElement(BusinessNamesSection, {
    query: queryFromUrl,
    presentation: twoStep ? 'list' : 'grid',
    onSelectedNameChange: handleSelectedNameChange,
    onUserSelectedName: handleUserSelectedName,
    onGenerateMore: handleGenerateMoreBi,
    onRegenerate: handleRegenerateBi,
    onTileTooltipView: handleTileTooltipView,
    onNamesShown: handleNamesShown
  })), !hasBusinessNamesError && /*#__PURE__*/React.createElement(React.Fragment, null, !queryFromUrl ? /*#__PURE__*/React.createElement(RecommendationsError, {
    error: "NoRecommendations",
    onRetry: onRetry,
    type: "anonymous",
    gridArea: "content",
    className: pageStyles.centeredContent
  }) : (!twoStep || mobileStep === 'domains') && /*#__PURE__*/React.createElement(BngDomainSuggestionsSection, {
    headingRef: domainsHeadingRef,
    recommendations: isBusinessNamesReady ? recommendations : [],
    isLoading: suggestionsIsLoading,
    isPricingLoading: isPricingLoading,
    hasMore: isBusinessNamesReady && hasNextPage,
    fetchNextPage: fetchNextPage,
    isFetchingNextPage: isFetchingNextPage,
    hasFetchError: Boolean(error && error !== 'NoRecommendations'),
    onRetry: onRetry,
    searchTerm: queryFromUrl,
    onBackToNames: twoStep ? handleBackToNames : undefined,
    animateMobileEntrance: twoStep
  }), queryFromUrl && recommendations.length > 0 && (!twoStep || mobileStep === 'domains') && /*#__PURE__*/React.createElement(Box, {
    gridArea: "footer",
    paddingTop: "SP6",
    paddingBottom: "SP6",
    paddingLeft: isMobile ? 'SP4' : '76px',
    paddingRight: isMobile ? 'SP4' : '48px',
    width: "100%",
    boxSizing: "border-box",
    align: "left"
  }, /*#__PURE__*/React.createElement(AnonymousFooter, {
    taxesNotIncluded: taxesNotIncluded
  })))));
};
//# sourceMappingURL=BngResultsPage.js.map